// Fix 49 (2026-10-06): contactos de iCloud — conexión y vista previa.
// La vista previa es SOLO LECTURA. Solo la ve el admin.
// Fix 50 (2026-10-06): sincronización — agrega a la agenda los clientes que
// faltan (automático cada hora + "Sincronizar ahora"). Nunca toca ni borra
// los contactos cargados a mano.
// Los textos viven acá (es/en) para no tocar LanguageContext.
import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Smartphone, PlugZap, Eye, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import { api } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';

const TEXT = {
  es: {
    title: 'Contactos de iCloud',
    subtitle: 'Clientes en la agenda de los iPhone de trabajo',
    readOnly: 'Solo se agregan los clientes que faltan. Los contactos cargados a mano nunca se modifican ni se borran.',
    account: 'Cuenta',
    configured: 'Contraseña de app cargada',
    notConfigured: 'Falta cargar ICLOUD_APP_PASSWORD en las variables de Emergent',
    test: 'Probar conexión',
    preview: 'Vista previa',
    testOk: (n) => `Conexión OK — ${n} contactos en la agenda`,
    running: (d, t) => (t ? `Leyendo contactos… ${d} de ${t}` : 'Leyendo contactos…'),
    lastRun: 'Última vista previa',
    icloudContacts: 'Contactos en iCloud',
    buyers: 'Clientes con teléfono',
    same: 'Ya están en la agenda',
    other: 'Mismo teléfono, otro nombre',
    missing: 'Faltan agregar',
    sameName: 'Mismo nombre, otro teléfono', // Fix 55
    sameNameSync: 'Mismo nombre (no se agregan)', // Fix 55
    otherTitle: 'Mismo teléfono, otro nombre en la agenda',
    phone: 'Teléfono', platformName: 'En la plataforma', agendaName: 'En la agenda',
    errors: {
      not_configured: 'Falta configurar la cuenta de iCloud.',
      auth_rejected: 'iCloud rechazó el Apple ID o la contraseña de app.',
      unreachable: 'No se pudo conectar con iCloud.',
      unexpected_response: 'iCloud respondió algo inesperado.',
      job_already_running: 'Ya hay una vista previa o sincronización en curso.',
      interrupted: 'La corrida anterior se interrumpió.',
      incomplete_read: 'La agenda no se leyó completa: no se escribió nada.',
      precondition_failed: 'El contacto cambió mientras se guardaba.',
      forbidden: 'iCloud no aceptó el contacto.',
    },
    // Fix 50 (2026-10-06): sincronización
    syncTitle: 'Agregar clientes a la agenda',
    syncAuto: 'Automático cada hora',
    syncNow: 'Sincronizar ahora',
    // Fix 51 (2026-10-06)
    syncOne: 'Probar con 1 contacto',
    confirmOne: 'Se va a agregar UN contacto real a la agenda de iCloud. Los contactos existentes no se tocan. ¿Seguir?',
    confirmAll: 'Se van a agregar a la agenda de iCloud todos los clientes que faltan (hasta 200 en esta corrida). Los contactos existentes no se tocan. ¿Seguir?',
    nextRun: 'Próxima corrida automática',
    syncHelp: 'Agrega solo los clientes cuyo teléfono no está en ningún contacto. "Sincronizar ahora" funciona aunque el automático esté apagado.',
    syncStarted: 'Sincronización iniciada',
    syncRunning: (d, t) => (t ? `Sincronizando… leyendo ${d} de ${t}` : 'Sincronizando…'),
    syncOn: 'Sincronización automática activada',
    syncOff: 'Sincronización automática desactivada',
    lastSync: 'Última sincronización',
    totalCreated: (n) => `${n ?? 0} contactos creados por la plataforma en total`,
    created: 'Agregados',
    updated: 'Actualizados',
    alreadyThere: 'Ya estaban (no se tocan)',
    deletedByTeam: 'Borrados del teléfono (no se recrean)',
    failed: 'Con error',
    deferred: 'Quedan para la próxima',
    createdTitle: 'Agregados en esta sincronización',
    name: 'Nombre',
  },
  en: {
    title: 'iCloud contacts',
    subtitle: 'Customers in the work iPhones address book',
    readOnly: 'Only missing customers are added. Contacts entered by hand are never changed or deleted.',
    account: 'Account',
    configured: 'App-specific password loaded',
    notConfigured: 'ICLOUD_APP_PASSWORD still has to be set in the Emergent variables',
    test: 'Test connection',
    preview: 'Preview',
    testOk: (n) => `Connection OK — ${n} contacts in the address book`,
    running: (d, t) => (t ? `Reading contacts… ${d} of ${t}` : 'Reading contacts…'),
    lastRun: 'Last preview',
    icloudContacts: 'Contacts in iCloud',
    buyers: 'Customers with phone',
    same: 'Already in the address book',
    other: 'Same phone, other name',
    missing: 'To be added',
    sameName: 'Same name, other phone', // Fix 55
    sameNameSync: 'Same name (not added)', // Fix 55
    otherTitle: 'Same phone, different name in the address book',
    phone: 'Phone', platformName: 'In the platform', agendaName: 'In the address book',
    errors: {
      not_configured: 'The iCloud account is not configured.',
      auth_rejected: 'iCloud rejected the Apple ID or the app-specific password.',
      unreachable: 'Could not reach iCloud.',
      unexpected_response: 'iCloud returned an unexpected response.',
      job_already_running: 'A preview or sync is already running.',
      interrupted: 'The previous run was interrupted.',
      incomplete_read: 'The address book was not fully read: nothing was written.',
      precondition_failed: 'The contact changed while it was being saved.',
      forbidden: 'iCloud did not accept the contact.',
    },
    // Fix 50 (2026-10-06): sync
    syncTitle: 'Add customers to the address book',
    syncAuto: 'Automatic every hour',
    syncNow: 'Sync now',
    // Fix 51 (2026-10-06)
    syncOne: 'Test with 1 contact',
    confirmOne: 'ONE real contact will be added to the iCloud address book. Existing contacts are not touched. Continue?',
    confirmAll: 'All missing customers will be added to the iCloud address book (up to 200 in this run). Existing contacts are not touched. Continue?',
    nextRun: 'Next automatic run',
    syncHelp: 'Only adds customers whose phone is not in any contact. "Sync now" works even when the automatic sync is off.',
    syncStarted: 'Sync started',
    syncRunning: (d, t) => (t ? `Syncing… reading ${d} of ${t}` : 'Syncing…'),
    syncOn: 'Automatic sync turned on',
    syncOff: 'Automatic sync turned off',
    lastSync: 'Last sync',
    totalCreated: (n) => `${n ?? 0} contacts created by the platform in total`,
    created: 'Added',
    updated: 'Updated',
    alreadyThere: 'Already there (not touched)',
    deletedByTeam: 'Deleted from the phone (not re-created)',
    failed: 'Failed',
    deferred: 'Left for next run',
    createdTitle: 'Added in this sync',
    name: 'Name',
  },
};

function errorText(T, err) {
  const reason = err?.response?.data?.detail?.reason || err;
  return T.errors[reason] || String(reason || 'error');
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-lg border p-3" data-testid={testid}>
      <div className="text-[11px] uppercase text-muted-foreground">{label}</div>
      <div className="text-xl font-semibold">{value ?? '—'}</div>
    </div>
  );
}

// Fix 51 (2026-10-06): la tarjeta vive en la pantalla "Agenda iCloud", que
// muestra la lista completa "Mismo teléfono, otro nombre" en una pestaña; por
// eso ahí se oculta la tabla de la tarjeta (showOtherNameTable={false}).
export default function ICloudContactsCard({ showOtherNameTable = true }) {
  const { lang } = useLang();
  const T = TEXT[lang === 'en' ? 'en' : 'es'];
  const qc = useQueryClient();
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const statusQ = useQuery({
    queryKey: ['icloud-status'],
    queryFn: async () => (await api.get('/admin/icloud/status')).data,
  });
  const previewQ = useQuery({
    queryKey: ['icloud-preview'],
    queryFn: async () => (await api.get('/admin/icloud/preview/status')).data,
    refetchInterval: (q) => (q.state.data?.status === 'running' ? 3000 : false),
  });

  const job = previewQ.data || {};
  const report = job.report;
  const running = job.status === 'running';

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = (await api.post('/admin/icloud/test')).data;
      setTestResult({ ok: true, text: T.testOk(res.contacts_in_icloud) });
    } catch (err) {
      setTestResult({ ok: false, text: errorText(T, err) });
    } finally {
      setTesting(false);
    }
  };

  const runPreview = async () => {
    try {
      await api.post('/admin/icloud/preview');
      qc.invalidateQueries({ queryKey: ['icloud-preview'] });
    } catch (err) {
      toast.error(errorText(T, err));
    }
  };

  const configured = statusQ.data?.configured;
  const prog = job.progress || {};

  // Fix 50 (2026-10-06): sincronización (switch cada hora + sincronizar ya).
  const [switching, setSwitching] = useState(false);
  const syncQ = useQuery({
    queryKey: ['icloud-sync'],
    queryFn: async () => (await api.get('/admin/icloud/sync/status')).data,
    refetchInterval: (q) => (q.state.data?.job?.status === 'running' ? 3000 : false),
  });
  const sync = syncQ.data || {};
  const sjob = sync.job || {};
  const srep = sjob.report;
  const syncRunning = sjob.status === 'running';
  const sprog = sjob.progress || {};

  const toggleSync = async (active) => {
    setSwitching(true);
    try {
      await api.post('/admin/icloud/sync/switch', null, { params: { active } });
      toast.success(active ? T.syncOn : T.syncOff);
      qc.invalidateQueries({ queryKey: ['icloud-sync'] });
    } catch (err) {
      toast.error(errorText(T, err));
    } finally {
      setSwitching(false);
    }
  };

  // Fix 51 (2026-10-06): las dos acciones que ESCRIBEN en la agenda real
  // piden confirmación. ``maxWrites`` = 1 es la prueba de un solo contacto.
  const runSync = async (maxWrites) => {
    if (!window.confirm(maxWrites === 1 ? T.confirmOne : T.confirmAll)) return;
    try {
      await api.post('/admin/icloud/sync', null,
        maxWrites ? { params: { max_writes: maxWrites } } : undefined);
      toast.success(T.syncStarted);
      qc.invalidateQueries({ queryKey: ['icloud-sync'] });
    } catch (err) {
      toast.error(errorText(T, err));
    }
  };

  return (
    <Card data-testid="icloud-contacts-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Smartphone className="h-4 w-4" /> {T.title}
        </CardTitle>
        <p className="text-[12px] text-muted-foreground">{T.subtitle}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2 text-[13px]">
          <span className="text-muted-foreground">{T.account}:</span>
          <span className="font-mono" data-testid="icloud-apple-id">
            {statusQ.data?.apple_id || '—'}
          </span>
          {configured
            ? <Badge variant="secondary">{T.configured}</Badge>
            : <Badge variant="destructive">{T.notConfigured}</Badge>}
        </div>
        <p className="text-[12px] text-muted-foreground">{T.readOnly}</p>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            onClick={runTest}
            disabled={!configured || testing}
            data-testid="icloud-test-button"
          >
            {testing
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <PlugZap className="h-4 w-4 mr-2" />}
            {T.test}
          </Button>
          <Button
            onClick={runPreview}
            disabled={!configured || running}
            data-testid="icloud-preview-button"
          >
            {running
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Eye className="h-4 w-4 mr-2" />}
            {T.preview}
          </Button>
        </div>

        {testResult && (
          <p
            className="text-[13px]"
            style={{ color: testResult.ok ? 'var(--success-fg)' : 'var(--danger-fg)' }}
            data-testid="icloud-test-result"
          >
            {testResult.text}
          </p>
        )}

        {running && (
          <p className="text-[13px] text-muted-foreground" data-testid="icloud-preview-running">
            {T.running(prog.done, prog.total)}
          </p>
        )}

        {job.status === 'error' && (
          <p className="text-[13px]" style={{ color: 'var(--danger-fg)' }}>
            {errorText(T, job.error)}
          </p>
        )}

        {report && !running && (
          <div className="space-y-3" data-testid="icloud-preview-report">
            <p className="text-[12px] text-muted-foreground">
              {T.lastRun}: {job.finished_at ? new Date(job.finished_at).toLocaleString() : '—'}
            </p>
            {/* Fix 55 (2026-10-08): + "Mismo nombre, otro teléfono" (no se agregan) */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
              <Stat label={T.icloudContacts} value={report.icloud?.contacts} testid="icloud-stat-contacts" />
              <Stat label={T.buyers} value={report.platform?.distinct_phones} testid="icloud-stat-buyers" />
              <Stat label={T.same} value={report.match?.already_same_name} testid="icloud-stat-same" />
              <Stat label={T.other} value={report.match?.already_other_name} testid="icloud-stat-other" />
              <Stat label={T.sameName} value={report.review?.same_name_other_phone} testid="icloud-stat-same-name" />
              <Stat label={T.missing} value={report.match?.missing} testid="icloud-stat-missing" />
            </div>
            {showOtherNameTable && (report.samples?.other_name || []).length > 0 && (
              <div>
                <div className="text-[13px] font-medium mb-1">{T.otherTitle}</div>
                <div className="max-h-[320px] overflow-auto rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{T.phone}</TableHead>
                        <TableHead>{T.platformName}</TableHead>
                        <TableHead>{T.agendaName}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.samples.other_name.map((r) => (
                        <TableRow key={r.phone}>
                          <TableCell className="font-mono text-xs whitespace-nowrap">{r.phone}</TableCell>
                          <TableCell className="text-xs">{r.platform_name}</TableCell>
                          <TableCell className="text-xs">{r.agenda_name}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Fix 50 (2026-10-06): sincronización — agrega los clientes que faltan */}
        <div className="space-y-3 border-t pt-4" data-testid="icloud-sync-section">
          <div className="text-[13px] font-medium">{T.syncTitle}</div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 rounded-md border border-border px-3 py-1.5">
              <Switch
                checked={!!sync.active}
                disabled={!configured || syncQ.isLoading || switching}
                onCheckedChange={(v) => toggleSync(!!v)}
                data-testid="icloud-sync-toggle"
              />
              <Label className="text-[12px] leading-tight">{T.syncAuto}</Label>
            </div>
            <Button
              variant="outline"
              onClick={() => runSync(1)}
              disabled={!configured || syncRunning}
              data-testid="icloud-sync-one-button"
            >
              {T.syncOne}
            </Button>
            <Button
              variant="secondary"
              onClick={() => runSync()}
              disabled={!configured || syncRunning}
              data-testid="icloud-sync-button"
            >
              {syncRunning
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <RefreshCw className="h-4 w-4 mr-2" />}
              {T.syncNow}
            </Button>
          </div>
          <p className="text-[12px] text-muted-foreground">{T.syncHelp}</p>
          {sync.active && sync.next_run_at && (
            <p className="text-[12px] text-muted-foreground" data-testid="icloud-sync-next-run">
              {T.nextRun}: {new Date(sync.next_run_at).toLocaleString()}
            </p>
          )}

          {syncRunning && (
            <p className="text-[13px] text-muted-foreground" data-testid="icloud-sync-running">
              {T.syncRunning(sprog.done, sprog.total)}
            </p>
          )}

          {sjob.status === 'error' && (
            <p className="text-[13px]" style={{ color: 'var(--danger-fg)' }} data-testid="icloud-sync-error">
              {errorText(T, sjob.error)}
            </p>
          )}

          {srep && !syncRunning && (
            <div className="space-y-3" data-testid="icloud-sync-report">
              <p className="text-[12px] text-muted-foreground">
                {T.lastSync}: {sjob.finished_at ? new Date(sjob.finished_at).toLocaleString() : '—'}
                {' · '}{T.totalCreated(sync.created_by_platform)}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                <Stat label={T.created} value={srep.done?.created} testid="icloud-sync-created" />
                <Stat label={T.updated} value={srep.done?.updated} testid="icloud-sync-updated" />
                <Stat label={T.alreadyThere} value={srep.planned?.already_in_agenda} testid="icloud-sync-already" />
                {/* Fix 55 (2026-10-08) */}
                <Stat label={T.sameNameSync} value={srep.planned?.same_name_other_phone ?? 0} testid="icloud-sync-same-name" />
                <Stat label={T.deletedByTeam} value={srep.planned?.deleted_by_team} testid="icloud-sync-deleted" />
                <Stat label={T.failed} value={srep.done?.failed} testid="icloud-sync-failed" />
                <Stat label={T.deferred} value={srep.planned?.deferred} testid="icloud-sync-deferred" />
              </div>
              {(srep.samples?.created || []).length > 0 && (
                <div>
                  <div className="text-[13px] font-medium mb-1">{T.createdTitle}</div>
                  <div className="max-h-[240px] overflow-auto rounded-lg border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{T.phone}</TableHead>
                          <TableHead>{T.name}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {srep.samples.created.map((r) => (
                          <TableRow key={r.phone}>
                            <TableCell className="font-mono text-xs whitespace-nowrap">{r.phone}</TableCell>
                            <TableCell className="text-xs">{r.name}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
